<template>
  <div>
    <va-modal
      ref="createOrUpdateFacility"
      :value="isOpen"
      :noOutsideDismiss="true"
      :okText="$t('modals.buttons.save')"
      :cancelText="$t('modals.buttons.back')"
      :dangerText="$t('modals.buttons.delete')"
      @ok="submitForm"
      @cancel="onCancel"
    >

        <div slot="header" class="modal-title">
          <span>{{ isUpdate ? $t('facility.edit_facility', {name: facility.name}) : $t('facility.create_facility') }}</span>
        </div>

        <form class="evollu-form">

          <div class="row">
            <va-input
              v-model="form.data.name"
              :placeholder="$t('staff.inputs.name')"
              id="name-input" required
            />
          </div>

          <div class="row">
            <va-input
              v-model="form.data.facility_code"
              id="facility-code-input"
              :placeholder="$t('facility.inputs.code')"
              required
            />
          </div>

          <div class="row">
            <vue-tel-input
              class="w-100"
              inputId="phone"
              v-model="form.data.phone"
              maxlength="20"
              :mode="'international'"
              :preferredCountries="countries.allowed"
              :disabledFetchingCountry="true"
              :defaultCountry="defaultCountrySelection"
              :placeholder="$t('people.inputs.phone')">
            </vue-tel-input>
          </div>

          <address-form-input
              :label="$t('patients.inputs.address.address')"
              :resource="{name: form.data.name}"
              v-model="form.data.address"
            >
            <span slot="button" v-if="!isUpdate">
              <span @click.prevent="syncFacility" class="pull-right">
                <i class="fa fa-location-arrow"></i>
              </span>
            </span>
            <span slot="otherFields" slot-scope="{ country }">
              <div v-if="country.code === 'PT'">
                <div class="row mb-3">
                  <multiselect
                    v-model="selectedDistrict"
                    :options="districts"
                    track-by="id"
                    label="name"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    :placeholder="$t('patients.inputs.address.distrito')"
                    :allow-empty="true"
                    :multiple="false"
                    :searchable="true"
                    >
                    <span slot="noOptions">---</span>
                    <template slot="clear" slot-scope="props">
                      <div class="multiselect__clear" v-if="selectedDistrict" @mousedown.prevent.stop="selectedDistrict = null"></div>
                    </template>
                    <span slot="noResult">{{$t('patients.noResults')}}</span>
                  </multiselect>
                </div>

                <div class="row mb-3">
                  <multiselect
                    v-model="selectedCouncil"
                    :options="councils"
                    track-by="id"
                    label="name"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    :placeholder="$t('patients.inputs.address.concelho')"
                    :allow-empty="true"
                    :multiple="false"
                    :searchable="true"
                    >
                    <template slot="clear" slot-scope="props">
                      <div class="multiselect__clear" v-if="selectedCouncil" @mousedown.prevent.stop="selectedCouncil = null"></div>
                    </template>
                    <span slot="noOptions">---</span>
                    <span slot="noResult">{{$t('patients.noResults')}}</span>
                  </multiselect>
                </div>

                <div class="row mb-3">
                  <multiselect
                    v-model="selectedParish"
                    :options="parishes"
                    track-by="id"
                    label="name"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    :placeholder="$t('patients.inputs.address.freguesia')"
                    :allow-empty="true"
                    :multiple="false"
                    :searchable="true"
                    >
                    <span slot="noOptions">---</span>
                    <template slot="clear" slot-scope="props">
                      <div class="multiselect__clear" v-if="selectedParish" @mousedown.prevent.stop="selectedParish = null"></div>
                    </template>
                    <span slot="noResult">{{$t('patients.noResults')}}</span>
                  </multiselect>
                </div>

              </div>
            </span>
          </address-form-input>
        </form>
      </va-modal>

    <ConfirmationModal
      v-if="showDeleteConfirmation"
      :title="$t('facility.delete?')"
      :body="$t('facility.delete_message', {name: this.facility.name})"
      @modal-canceled="closeDelete"
      @modal-confirmed="confirmDelete"
    />

  </div>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import { VueTelInput } from 'vue-tel-input'
import Multiselect from 'vue-multiselect'
import { PHONE_REGEX } from '@/utils/regex'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import AddressFormInput from '@/components/patients/AddressFormInput'
import { FacilityService, ZonesService } from '@/api/AuthService'

export default {
  components: {
    Multiselect,
    VueTelInput,
    AddressFormInput,
    ConfirmationModal
  },
  props: {
    entity: {
      type: Object,
      required: false
    },
    facility: {
      type: Object,
      required: false
    }
  },
  data: function () {
    return {
      form: {
        data: {
          name: null,
          phone: null,
          facility_code: null,
          address: {
            line1: null,
            line2: null,
            city: null,
            postal_code: null,
            lat: null,
            lng: null,
            country: null
          }
        }
      },
      districts: [],
      councils: [],
      parishes: [],
      selectedDistrict: null,
      selectedCouncil: null,
      selectedParish: null,
      showDeleteConfirmation: false,
      isOpen: false
    }
  },
  created: function () {
    if (this.isUpdate) {
      this.prepareForUpdate()
    } else {
      this.getDistricts()
    }
  },
  mounted: function () {
    this.isOpen = true
  },
  computed: {
    ...mapState({
      'countries': (state) => state.countries
    }),
    isUpdate: function () {
      return this.facility !== null && this.facility !== undefined
    },
    authUserEntity: function () {
      return this.$auth.user().entity
    },
    defaultCountrySelection: function () {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country
      }
      return this.$store.state.countries.default
    },
    invalidPhone: function () {
      if (this.form.data.phone) {
        return !this.form.data.phone.replace(new RegExp("\\s", "g"), '').match(PHONE_REGEX)
      }
      return false
    }
  },
  methods: {
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('cancel')
      }, 500)
    },
    distritoSelected (option) {
      if (this.selectedParish) this.selectedParish = null
      if (this.selectedCouncil) this.selectedCouncil = null
      this.$http({
        url: this.$store.state.evollauth_server + '/distritos/' + option.id
      }).then((response) => {
        this.councils = response.data.data.concelhos
      }).catch((error) => {
        console.log(error)
      })
    },
    concelhoSelected (option) {
      if (this.selectedParish) this.selectedParish = null
      this.$http({
        url: this.$store.state.evollauth_server + '/concelhos/' + option.id
      }).then((response) => {
        this.parishes = response.data.data.freguesias
      }).catch((error) => {
        console.log(error)
      })
    },
    prepareForUpdate: function () {
      this.form.data.name = this.facility.name
      this.form.data.facility_code = this.facility.facility_code
      this.form.data.phone = this.facility.phone

      if (this.facility.address) {
        this.form.data.address = JSON.parse(JSON.stringify(this.facility.address))

        if (this.facility.address.zone_code) {
          let zonesCode = this.facility.address.zone_code
          let codeDistrito = zonesCode.length >= 2 ? zonesCode.substr(0, 2) : null
          let codeConcelho = zonesCode.length >= 4 ? zonesCode.substr(0, 4) : null
          let codeFreguesia = zonesCode.length >= 6 ? zonesCode.substr(0, 6) : null

          this.getDistricts()
            .then(() => {
              this.selectedDistrict = this.districts.find(distrito => distrito.id === codeDistrito)
              if (this.selectedDistrict) {
                ZonesService.getPortugueseCouncils(codeDistrito)
                  .then(councils => {
                    this.councils = councils
                    this.selectedCouncil = this.councils.find(concelho => concelho.id === codeConcelho)
                    if (this.selectedCouncil && codeFreguesia !== null) {
                      ZonesService.getPortugueseCouncils(codeConcelho.substr())
                        .then(parishes => {
                          this.parishes = parishes
                          this.selectedParish = this.parishes.find(freguesia => freguesia.id === codeFreguesia)
                        }).catch((error) => {
                          console.log(error)
                        })
                    }
                  }).catch((error) => {
                    console.log(error)
                  })
              }
            })
        }
      }
    },
    getFormData: function () {
      let body = JSON.parse(JSON.stringify(this.form.data))

      if (!body.name) {
        this.showToast(this.$t('facility.errors.invalid_name'), this.$store.state.errorToastOptions)
        return null
      }

      if (!body.phone || this.invalidPhone) {
        this.showToast(this.$t('phone_input.defaults.invalid_phone'), this.$store.state.errorToastOptions)
        return null
      }

      body.entity = this.entity ? this.entity.id : (this.facility ? this.facility.entity_id : null)

      let zoneCode = null

      if (this.selectedParish) {
        zoneCode = this.selectedParish.id
      } else if (this.selectedCouncil) {
        zoneCode = this.selectedCouncil.id
      } else if (this.selectedDistrict) {
        zoneCode = this.selectedDistrict.id
      }

      body.address.zone_code = zoneCode

      return body
    },
    submitForm: function () {
      let data = this.getFormData()

      if (data) {
        (this.isUpdate ? FacilityService.update(this.facility.id, data) : FacilityService.store(data))
          .then(facility => {
            this.$refs.createOrUpdateFacility.cancel()
            this.$emit(this.isUpdate ? 'facility-updated' : 'facility-created', { facility })
            if (this.isUpdate) {
              this.$emit('facility-updated', { facility })
              this.showToast(this.$t('facility.updated'), this.$store.state.successToastOptions)
            } else {
              this.$emit('facility-created', { facility })
              this.showToast(this.$t('facility.created'), this.$store.state.successToastOptions)
            }
            this.$refs.createOrUpdateFacility.cancel()
          })
          .catch(errors => {
            console.log(errors)
            this.showToast(this.$t('facility.something_went_wrong'), this.$store.state.errorToastOptions)
          })
      }
    },
    closeDelete: function () {
      this.showDeleteConfirmation = false
    },
    confirmDelete: function () {
      FacilityService.delete(this.facility.id)
        .then(() => {
          this.showDeleteConfirmation = false
          this.showToast(this.$t('facility.deleted'), this.$store.state.successToastOptions)
          this.$emit('facility-deleted')
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('facility.something_went_wrong'), this.$store.state.errorToastOptions)
          this.showDeleteConfirmation = false
        })
    },
    syncFacility: function () {
      this.$http({
        url: this.$store.state.ebooking_server +
          '/syncfacility/' +
          this.facility.id
      }).then((response) => {
        let facilityInfo = response.data.data
        this.form.data.address.line1 = facilityInfo.rua.trim()
        this.form.data.address.line2 = facilityInfo.numero.trim()
        this.form.data.address.city = facilityInfo.localidade.trim()
        this.form.data.address.postal_code = facilityInfo.cp_zona.trim() + '-' + facilityInfo.cp_sufixo.trim() + ' ' + facilityInfo.localidadepostal.trim()
        this.form.facility_code = 'ANF ' + facilityInfo.cod_anf.trim()
        this.selectedDistrict = this.districts.filter(distrito => distrito.name === facilityInfo.distrito.trim())[0]
        if (this.selectedDistrict) {
          ZonesService.getPortugueseCouncils(this.selectedDistrict.id)
            .then(councils => {
              this.councils = councils
              this.selectedCouncil = this.councils.filter(concelho => concelho.name === facilityInfo.concelho.trim())[0]
              if (this.selectedCouncil) {
                ZonesService.getPortugueseParishes(this.selectedDistrict.id)
                  .then(parishes => {
                    this.parishes = parishes
                    this.selectedParish = this.parishes.filter(freguesia => freguesia.name === facilityInfo.freguesia.trim())[0]
                  }).catch((error) => {
                    console.log(error)
                  })
              }
            }).catch((error) => {
              console.log(error)
            })
        }
      }).catch((error) => {
        console.log(error)
      })
    },
    getDistricts: function () {
      return new Promise((resolve, reject) => {
        ZonesService.getPortugueseDistricts()
          .then(districts => {
            this.districts = districts
          })
          .catch((error) => {
            console.log(error)
            this.districts = []
          })
          .finally(() => {
            resolve()
          })
      })
    }
  },
  watch: {
    'form.data.address.country': function (value) {
      if (value !== 'PT') {
        this.selectedDistrict = null
        this.selectedCouncil = null
        this.selectedParish = null
      }
    },
    selectedDistrict: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.councils = []
        this.selectedCouncil = null
        this.selectedParish = null
      }

      if (newValue) {
        ZonesService.getPortugueseCouncils(newValue.id)
          .then(councils => {
            console.log(councils)
            this.councils = councils
          })
      }
    },
    selectedCouncil: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.parishes = []
        this.selectedParish = null
      }

      if (newValue) {
        ZonesService.getPortugueseParishes(newValue.id)
          .then(parishes => {
            console.log(parishes)
            this.parishes = parishes
          })
      }
    }
  }
}
</script>
